import { useMutation } from '@apollo/client';
import type {
  UpdateIssueUpdateMutation,
  UpdateIssueUpdateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateIssueUpdateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateIssueUpdateTRPCInput } from './useUpdateIssueUpdateTRPC';
import { useUpdateIssueUpdateTRPC } from './useUpdateIssueUpdateTRPC';

export const useUpdateIssueUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateIssueUpdateGraphQL, graphqlState] = useMutation(
    UpdateIssueUpdateDocument,
    {
      update: (cache) => evictField(cache, 'issue_update'),
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateIssueUpdateTRPC();

  const updateIssueUpdate = async (
    variables: UpdateIssueUpdateMutationVariables
  ): Promise<UpdateIssueUpdateMutation> => {
    if (trpcEnabled) {
      const trpcInput: UpdateIssueUpdateTRPCInput = {
        Id: variables.Id,
        ParentIssueId: variables.ParentIssueId,
        Title: variables.Title,
        Description: variables.Description,
        CustomAttributeData: variables.CustomAttributeData ?? null,
        OriginalTimestamp: variables.OriginalTimestamp,
      };

      return trpcMutation.updateIssueUpdate(trpcInput);
    }

    const result = await updateIssueUpdateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update issue update');
    }
    if (result.data.update_issue_update?.affected_rows !== 1) {
      throw new Error(
        'Records not updated. Record may have been updated by another user'
      );
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateIssueUpdate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateIssueUpdate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
